import React from "react";
import PropTypes from "prop-types";
import { FiEdit2 } from "react-icons/fi";
import styles from "./IBODetailsCard.module.css";

/**
 * Contact details the directory can't always supply. `Members.IBOInfo` has no
 * verified address or phone for an IBO and CRM returns null for most contact
 * emails, so these render "N/A" rather than an empty row — a blank looked like
 * the screen had failed to load. See storeIbosSql.js for why they're empty:
 * inventing a plausible value is the bug that module exists to prevent.
 */
function ContactValue({ value }) {
  const text = typeof value === "string" ? value.trim() : value;
  if (text) return <span className={styles.value}>{text}</span>;
  return (
    <span
      className={`${styles.value} ${styles.valueMissing}`}
      title="Not available from the IBO directory."
    >
      N/A
    </span>
  );
}

ContactValue.propTypes = {
  value: PropTypes.string,
};

export function IBODetailsCard({
  name,
  address,
  isTempAddress,
  onEditAddress,
  phoneNum,
  email,
  subTotal,
  tax,
  total,
}) {
  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.heading}>IBO Details</h3>
      </header>

      <div className={styles.row}>
        <span className={styles.label}>Name</span>
        <ContactValue value={name} />
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Address</span>
        <span className={styles.addressValue}>
          <ContactValue value={address} />
          {isTempAddress ? (
            <span
              className={styles.tempTag}
              title="A one-off address for this order. It is not saved to the IBO's record in CRM."
            >
              Temporary
            </span>
          ) : null}
          {onEditAddress ? (
            <button
              type="button"
              className={styles.editAddress}
              onClick={onEditAddress}
              aria-label="Edit shipping address"
              title="Choose a different address for this order."
            >
              <FiEdit2 size={14} />
            </button>
          ) : null}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Phone</span>
        <ContactValue value={phoneNum} />
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Email</span>
        <ContactValue value={email} />
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Subtotal</span>
        <span className={styles.value}>{subTotal}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Tax</span>
        <span className={styles.value}>{tax}</span>
      </div>
      <div className={`${styles.row} ${styles.totalRow}`}>
        <span className={styles.label}>Total</span>
        <span className={styles.value}>{total}</span>
      </div>
    </section>
  );
}

IBODetailsCard.propTypes = {
  name: PropTypes.string,
  address: PropTypes.string,
  isTempAddress: PropTypes.bool,
  /** Omit to render the address read-only (no pencil). */
  onEditAddress: PropTypes.func,
  phoneNum: PropTypes.string,
  email: PropTypes.string,
  subTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
